import type { Scene } from '@babylonjs/core/scene';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { CreatePlane } from '@babylonjs/core/Meshes/Builders/planeBuilder';
import { CreateTorus } from '@babylonjs/core/Meshes/Builders/torusBuilder';
import { CreateDisc } from '@babylonjs/core/Meshes/Builders/discBuilder';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Action, type ArenaState, type BombState } from '@arena/shared';
import type { MoveEffects } from './moveEffects.ts';

// Kept within the HD-2D camera's vertical field, above the tallest combatants.
const FLIGHT_HEIGHT=4.8;

class ArtSprite {
  readonly mesh:Mesh;
  facing=1;
  private readonly texture:Texture;
  private readonly material:StandardMaterial;
  constructor(scene:Scene,kind:string,size:number){
    this.mesh=CreatePlane(`mussolini-${kind}`,{size},scene);
    this.mesh.billboardMode=Mesh.BILLBOARDMODE_ALL;this.mesh.isPickable=false;
    this.texture=new Texture(`/proto2d/mussolini/${kind}.png`,scene,true,true,Texture.NEAREST_SAMPLINGMODE);
    this.texture.hasAlpha=true;this.texture.wrapU=this.texture.wrapV=Texture.CLAMP_ADDRESSMODE;
    this.material=new StandardMaterial(`mussolini-${kind}`,scene);
    this.material.diffuseTexture=this.texture;this.material.useAlphaFromDiffuseTexture=true;
    this.material.disableLighting=true;this.material.emissiveColor=Color3.White();this.material.backFaceCulling=false;
    this.mesh.material=this.material;
  }
  place(x:number,y:number,z:number,flip=false){this.mesh.position.set(x,y,z);this.facing=flip?-1:1;this.texture.uScale=flip?-1:1;this.texture.uOffset=flip?1:0;}
  dispose(){this.mesh.dispose();this.material.dispose();this.texture.dispose();}
}

class BombView {
  readonly ring:Mesh;readonly fill:Mesh;readonly sprite:ArtSprite;
  private readonly material:StandardMaterial;
  private lastTicks=-1;private extrapolate=0;
  constructor(scene:Scene,state:BombState){
    this.ring=CreateTorus('bomb-danger',{diameter:state.radius*2,thickness:0.065,tessellation:48},scene);
    this.fill=CreateDisc('bomb-area',{radius:state.radius,tessellation:48},scene);this.fill.rotation.x=Math.PI/2;
    this.material=new StandardMaterial('bomb-danger',scene);this.material.disableLighting=true;
    this.material.emissiveColor=Color3.FromHexString('#ff572d');this.material.backFaceCulling=false;
    this.ring.material=this.material;
    const tint=new StandardMaterial('bomb-area',scene);tint.disableLighting=true;tint.emissiveColor=Color3.FromHexString('#ff3925');
    tint.alpha=0.13;tint.backFaceCulling=false;this.fill.material=tint;
    this.ring.isPickable=this.fill.isPickable=false;
    this.sprite=new ArtSprite(scene,'bomb',1.15);
  }
  update(b:BombState,dt:number){
    if(b.ticks!==this.lastTicks){this.lastTicks=b.ticks;this.extrapolate=0;}else this.extrapolate=Math.min(this.extrapolate+dt,0.08);
    const ticks=Math.max(0,b.ticks-this.extrapolate*30),visible=ticks<=42;
    this.ring.setEnabled(visible);this.fill.setEnabled(visible);this.sprite.mesh.setEnabled(ticks<=24);
    this.ring.position.set(b.x,b.y+0.055,b.z);this.fill.position.set(b.x,b.y+0.035,b.z);
    this.material.alpha=0.65+0.35*Math.sin((42-ticks)*0.7);
    this.sprite.place(b.x,b.y+0.4+Math.max(0,FLIGHT_HEIGHT-b.y-0.4)*(ticks/24)**2,b.z);
  }
  dispose(){this.ring.dispose(false,true);this.fill.dispose(false,true);this.sprite.dispose();}
}

/** Scene objects follow replicated channels and bomb timers, including after reconnect. */
export class MussoliniScene {
  private readonly scene:Scene;
  private readonly effects:MoveEffects;
  private readonly photos=new Map<string,ArtSprite[]>();
  private readonly bombs=new Map<string,BombView>();
  private readonly planes=new Map<string,ArtSprite>();
  constructor(scene:Scene,effects:MoveEffects){this.scene=scene;this.effects=effects;}
  flash(caster:string){
    const right=this.scene.activeCamera?.getDirection(Vector3.Right())??Vector3.Right();
    for(const photographer of this.photos.get(caster)??[]){
      const p=photographer.mesh.position,offset=photographer.facing*0.22;
      this.effects.burst('mussolini-flash',p.x+right.x*offset,p.y+0.62*photographer.mesh.scaling.y,p.z+right.z*offset,0.43);
    }
  }
  update(state:ArenaState,dt:number){
    const activePhotos=new Set<string>();
    const right=this.scene.activeCamera?.getDirection(Vector3.Right())??Vector3.Right();
    for(const [id,p]of state.players){
      if(p.characterId!=='mussolini' || p.action!==Action.Special1 || p.hitstun>0 || p.knockedOut || p.actionTick<6 || p.actionTick>=55)continue;
      activePhotos.add(id);
      if(!this.photos.has(id)){
        const sprites=[[-1.55,0.65],[1.55,0.65],[0,-1.65]].map(([dx,dz])=>{
          const a=new ArtSprite(this.scene,'photographer',1.65);
          const forward=this.scene.activeCamera?.getDirection(Vector3.Forward())??Vector3.Forward();
          const tilt=Math.max(0.55,Math.hypot(forward.x,forward.z));
          a.mesh.billboardMode=Mesh.BILLBOARDMODE_Y;a.mesh.scaling.y=1/tilt;
          a.place(p.x+dx!,p.y+0.72/tilt,p.z+dz!,dx!*right.x+dz!*right.z>0);return a;
        });this.photos.set(id,sprites);
      }
    }
    for(const [id,sprites]of this.photos)if(!activePhotos.has(id)){sprites.forEach(s=>s.dispose());this.photos.delete(id);}
    const lanes=new Map<string,BombState>();
    for(const [id,b]of state.bombs){
      let view=this.bombs.get(id);if(!view){view=new BombView(this.scene,b);this.bombs.set(id,view);}view.update(b,dt);
      const key=`${b.strike}-${b.lane}`,prior=lanes.get(key);
      if(!prior || b.ticks<prior.ticks)lanes.set(key,b);
    }
    for(const [id,v]of this.bombs)if(!state.bombs.has(id)){v.dispose();this.bombs.delete(id);}
    for(const [id,b]of lanes){
      let plane=this.planes.get(id);if(!plane){plane=new ArtSprite(this.scene,'plane',5.5);this.planes.set(id,plane);}
      // Drop point is 24 ticks before impact; the aircraft continues straight.
      const age=b.duration-b.ticks;
      const x=b.x+(24-b.ticks)*b.flightX/30;
      const z=b.z+(24-b.ticks)*b.flightZ/30;
      plane.place(x,FLIGHT_HEIGHT,z,b.flightX*right.x+b.flightZ*right.z<0);
      plane.mesh.setEnabled(age>=b.lane*28 && b.ticks<80);
    }
    for(const [id,p]of this.planes)if(!lanes.has(id)){p.dispose();this.planes.delete(id);}
  }
  clear(){
    for(const p of this.photos.values())p.forEach(v=>v.dispose());this.photos.clear();
    for(const b of this.bombs.values())b.dispose();this.bombs.clear();
    for(const p of this.planes.values())p.dispose();this.planes.clear();
  }
}
